import { containerNames } from '../../constants';
import { PixiBase } from '../../core/pixi/PixiBase';
import { setTranslationValues } from '../../core/helpers';
import { gameStore } from '../../store/gameStore';
import { iframeService } from '../../services/iframeService';
import { isMobile } from '../../helpers/isMobile';
import { spinBarStore } from '../../store/spinBarStore';
import { applicationStore } from '../../store/applicationStore';
import { ValidationType } from '../../enums/validationType';

export class NotificationDrawer {
  constructor() {
    this.animations = {};
    this.NotificationContainer = PixiBase.createContainer(containerNames.NotificationContainer);
  }

  createNotificationImg({ texture, config }) {
    const notificationImg = PixiBase.createSprite(texture);
    const { x, y, anchor } = config;
    PixiBase.setPosition(notificationImg, { x, y });
    notificationImg.anchor.set(anchor);
    return notificationImg;
  }

  createNotificationBackground({ texture, config }) {
    const notificationBackground = PixiBase.createSprite(texture);
    const { x, y, anchor } = config;
    PixiBase.setPosition(notificationBackground, { x: x, y: y });
    notificationBackground.anchor.set(anchor);
    return notificationBackground;
  }

  createNotificationText({ text, styles, config }) {
    const notificationText = PixiBase.createText(text, styles);
    const { x, y, anchor } = config;
    PixiBase.setPosition(notificationText, { x: x, y: y });
    notificationText.anchor.set(anchor);
    return notificationText;
  }

  toggleMaintenanceNotification(bool) {
    this.maintenanceContainer.visible = bool;
  }

  toggleBetNotAcceptedNotification(bool, validationType = false) {
    this.betNotAcceptedContainer.visible = bool;
    if (validationType) {
      this.updateBetNotAcceptedNotification(validationType);
    }
  }

  createMaintenanceNotification() {
    this.maintenanceContainer = PixiBase.createContainer('maintenance');
    const maintenanceBackground = this.createNotificationBackground({
      texture: isMobile() ? this.spriteSheet.largeMaintenance : this.spriteSheet.maintenance,
      config: this.config.NotificationContainer.maintenance,
    });
    const maintenanceImgTexture = this.createNotificationImg({
      texture: this.spriteSheet.shapeWarning,
      config: this.config.NotificationContainer.maintenanceImg,
    });
    const maintenanceText = this.createNotificationText({
      text: setTranslationValues(gameStore.canvasTranslationKeys.MAINTENANCE, { VALUE: 10 }),
      styles: {
        fontSize: isMobile() ? 13 : 18,
        fontWeight: 400,
        fontFamily: 'Lato',
        fill: '#FFFFFF',
        align: 'left',
        wordWrap: true,
        wordWrapWidth: maintenanceBackground.width - maintenanceImgTexture.width - 10,
      },
      config: this.config.NotificationContainer.maintenanceText,
    });
    maintenanceText.style.fontSize =
      (isMobile() ? 13 : 18) *
      Math.min(
        (maintenanceBackground.width - maintenanceImgTexture.width) / maintenanceText.width,
        maintenanceBackground.height / maintenanceText.height
      );
    this.maintenanceText = maintenanceText;
    this.maintenanceContainer.addChild(maintenanceBackground);
    this.maintenanceContainer.addChild(maintenanceImgTexture);
    this.maintenanceContainer.addChild(maintenanceText);
    this.maintenanceContainer.visible = false;
    this.NotificationContainer.addChild(this.maintenanceContainer);
  }

  updateMaintenanceTimeValue(value) {
    this.maintenanceText.text = setTranslationValues(gameStore.canvasTranslationKeys.MAINTENANCE, {
      VALUE: value,
    });
  }

  startUnderMaintenanceFunctional({ startDate, notifySeconds }) {
    if (startDate) {
      let seconds = Math.floor(
        (+new Date(`${startDate}${startDate.includes('Z') ? '' : 'Z'}`) - +new Date()) / 1000
      );

      if (!notifySeconds || (notifySeconds > seconds && seconds >= 0)) {
        this.toggleMaintenanceNotification(true);
        this.updateMaintenanceTimeValue(seconds);
      }

      const interval = setInterval(() => {
        if (seconds >= 0) {
          if (notifySeconds === seconds) {
            this.toggleMaintenanceNotification(true);
          }
          if (!notifySeconds || notifySeconds >= seconds) {
            this.updateMaintenanceTimeValue(seconds);
          }
          seconds -= 1;
        } else {
          clearInterval(interval);
          if (spinBarStore.isSpinStart) {
            applicationStore.setIsMaintenance(true);
          } else {
            iframeService.reloadFrame();
          }
        }
      }, 1000);
    }
  }

  updateBetNotAcceptedNotification(validationType) {
    this.betNotAcceptedContainerText.text =
      validationType === ValidationType.MoneyTransactionError
        ? gameStore.canvasTranslationKeys.BET_NOT_ACCEPTED
        : gameStore.canvasTranslationKeys.TECHNICAL_ISSUES;
    this.updateBetNotAcceptedNotificationPosition();
  }

  updateBetNotAcceptedNotificationPosition() {
    const margin = isMobile() ? 5 : 10;
    this.betNotAcceptedContainerText.style.fontSize =
      this.betNotAcceptedContainerText.style.fontSize *
      Math.min(
        (this.betNotAcceptedBackground.width -
          this.betNotAcceptedContainerImgTexture.width -
          margin) /
          this.betNotAcceptedContainerText.width,
        this.betNotAcceptedBackground.height / this.betNotAcceptedContainerText.height
      );
  }

  createBetNotAcceptedNotification() {
    this.betNotAcceptedContainer = PixiBase.createContainer('betNotification');
    this.betNotAcceptedBackground = this.createNotificationBackground({
      texture: this.spriteSheet.notificationError,
      config: this.config.NotificationContainer.notification,
    });
    this.betNotAcceptedContainerImgTexture = this.createNotificationImg({
      texture: this.spriteSheet.shapeError,
      config: this.config.NotificationContainer.notificationImg,
    });
    this.betNotAcceptedContainerText = this.createNotificationText({
      text: gameStore.canvasTranslationKeys.BET_NOT_ACCEPTED,
      styles: {
        fontSize: 18,
        fontWeight: 400,
        fontFamily: 'Roboto',
        fill: '#FFFFFF',
      },
      config: this.config.NotificationContainer.notificationText,
    });
    this.updateBetNotAcceptedNotificationPosition();
    this.betNotAcceptedContainer.addChild(this.betNotAcceptedBackground);
    this.betNotAcceptedContainer.addChild(this.betNotAcceptedContainerImgTexture);
    this.betNotAcceptedContainer.addChild(this.betNotAcceptedContainerText);
    this.NotificationContainer.addChild(this.betNotAcceptedContainer);
    this.betNotAcceptedContainer.visible = false;
  }

  execute(config, spriteSheet) {
    const [gameImageSpriteSheet] = spriteSheet;
    this.config = config;
    this.spriteSheet = gameImageSpriteSheet;
    this.createMaintenanceNotification();
    this.createBetNotAcceptedNotification();
  }
}
